<template>
  <div class="flex-1 flex flex-col overflow-hidden">
    <div class="flex items-center px-4 py-3 gap-2 border-b border-n-weak">
      <div class="flex-1 relative">
        <svg
          class="absolute left-2.5 top-1/2 -translate-y-1/2 text-n-slate-9"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
        >
          <circle cx="7" cy="7" r="4.5" />
          <path d="M10.5 10.5L14 14" />
        </svg>
        <input
          v-model="searchQuery"
          :placeholder="
            showUserPicker ? 'Buscar pessoa...' : 'Buscar conversa...'
          "
          class="w-full bg-n-alpha-black2 outline outline-1 outline-n-weak rounded-lg text-n-slate-12 pl-8 pr-3 py-2 text-sm placeholder:text-n-slate-10 focus:outline-n-brand transition-all"
        />
      </div>
      <button
        class="flex items-center justify-center size-9 rounded-lg transition-all"
        :class="
          showUserPicker
            ? 'bg-n-brand text-white shadow-sm'
            : 'text-n-slate-11 hover:bg-n-alpha-2'
        "
        :title="showUserPicker ? 'Voltar' : 'Nova conversa'"
        @click="showUserPicker = !showUserPicker"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
        >
          <template v-if="showUserPicker">
            <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" />
          </template>
          <template v-else>
            <path d="M9 4.5V13.5M4.5 9H13.5" />
          </template>
        </svg>
      </button>
    </div>

    <div v-if="showUserPicker" class="flex-1 overflow-y-auto py-1">
      <div
        v-if="filteredUsers.length === 0"
        class="text-n-slate-10 text-center py-10 text-sm"
      >
        Nenhum usuário encontrado
      </div>
      <div
        v-for="user in filteredUsers"
        :key="user.id"
        class="flex items-center px-4 py-3 gap-3 cursor-pointer hover:bg-n-alpha-1 transition-colors group"
        @click="startConversation(user.id)"
      >
        <div class="relative flex-shrink-0">
          <img
            v-if="user.avatar_url && !brokenAvatars.has(user.avatar_url)"
            :src="user.avatar_url"
            class="size-10 rounded-full object-cover ring-1 ring-n-weak group-hover:ring-n-brand/40 transition-all"
            @error="onAvatarError(user.avatar_url)"
          />
          <div
            v-else
            class="size-10 rounded-full text-white flex items-center justify-center font-semibold text-base"
            style="background: linear-gradient(135deg, #3b82f6, #4f46e5);"
          >
            {{ user.name?.charAt(0)?.toUpperCase() }}
          </div>
          <span
            class="absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-white dark:border-n-background"
            :class="
              user.availability === 'online'
                ? 'bg-green-500'
                : 'bg-n-slate-8'
            "
          />
        </div>
        <div class="flex-1 min-w-0 flex flex-col">
          <span class="text-n-slate-12 font-medium text-sm truncate">{{
            user.name
          }}</span>
          <span class="text-n-slate-10 text-xs truncate mt-0.5">{{
            user.email
          }}</span>
        </div>
        <svg
          class="text-n-slate-9 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path d="M6 3L11 8L6 13" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
    </div>

    <div v-else class="flex-1 overflow-y-auto py-1">
      <div
        v-if="isFetching"
        class="text-n-slate-10 text-center py-10 text-sm"
      >
        <div class="inline-block size-5 border-2 border-n-slate-8 border-t-n-brand rounded-full animate-spin" />
        <p class="mt-2">Carregando...</p>
      </div>
      <div
        v-else-if="filteredConversations.length === 0"
        class="text-center py-14 px-6"
      >
        <div class="text-4xl mb-3">💬</div>
        <p class="text-n-slate-10 text-sm">
          {{
            searchQuery
              ? 'Nenhuma conversa encontrada'
              : 'Nenhuma conversa ainda'
          }}
        </p>
        <p
          v-if="!searchQuery"
          class="text-n-slate-9 text-xs mt-1"
        >
          Clique no + para iniciar uma conversa
        </p>
      </div>
      <div
        v-for="conv in filteredConversations"
        :key="conv.id"
        class="flex items-center px-4 py-3 gap-3 cursor-pointer hover:bg-n-alpha-1 transition-colors group"
        :class="conv.system ? 'bg-n-alpha-1/50' : ''"
        @click="$emit('select', conv.id)"
      >
        <div class="relative flex-shrink-0">
          <div
            v-if="conv.system"
            class="size-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center text-lg shadow-sm"
          >
            🔔
          </div>
          <template v-else>
            <img
              v-if="conv.other_user.avatar_url && !brokenAvatars.has(conv.other_user.avatar_url)"
              :src="conv.other_user.avatar_url"
              class="size-10 rounded-full object-cover ring-1 ring-n-weak group-hover:ring-n-brand/40 transition-all"
              @error="onAvatarError(conv.other_user.avatar_url)"
            />
            <div
              v-else
              class="size-10 rounded-full text-white flex items-center justify-center font-semibold text-base"
              style="background: linear-gradient(135deg, #3b82f6, #4f46e5);"
            >
              {{ conv.other_user.name?.charAt(0)?.toUpperCase() }}
            </div>
            <span
              class="absolute -bottom-0.5 -right-0.5 size-3 rounded-full border-2 border-white dark:border-n-background"
              :class="
                conv.other_user.availability === 'online'
                  ? 'bg-green-500'
                  : 'bg-n-slate-8'
              "
            />
          </template>
        </div>
        <div class="flex-1 min-w-0 flex flex-col">
          <span
            class="text-n-slate-12 font-medium text-sm truncate"
            :class="conv.system ? 'font-semibold' : ''"
          >
            {{
              conv.system
                ? conv.system_name || 'Notificações CRM'
                : conv.other_user.name
            }}
          </span>
          <span
            v-if="conv.last_message"
            class="text-n-slate-10 text-xs truncate mt-0.5"
            >{{ conv.last_message.content }}</span
          >
          <span
            v-else
            class="text-n-slate-10 text-xs truncate mt-0.5 italic"
            >{{
              conv.system ? 'Sem notificações' : 'Sem mensagens'
            }}</span
          >
        </div>
        <div class="flex flex-col items-end gap-1 flex-shrink-0">
          <span
            v-if="conv.last_message"
            class="text-n-slate-9 text-[11px]"
            >{{ formatTime(conv.last_message.created_at) }}</span
          >
          <span
            v-if="conv.unread_count > 0"
            class="bg-n-brand text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center shadow-sm"
          >
            {{ conv.unread_count }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useStore } from 'vuex';

defineEmits(['select']);

const store = useStore();
const searchQuery = ref('');
const brokenAvatars = reactive(new Set());

function onAvatarError(url) {
  if (url) brokenAvatars.add(url);
}
const showUserPicker = ref(false);

const conversations = computed(
  () => store.getters['internalChat/getConversations']
);
const availableUsers = computed(
  () => store.getters['internalChat/getAvailableUsers']
);
const isFetching = computed(
  () => store.getters['internalChat/getUIFlags'].isFetchingConversations
);

const filteredConversations = computed(() => {
  if (!searchQuery.value) return conversations.value;
  const q = searchQuery.value.toLowerCase();
  return conversations.value.filter(c =>
    c.system
      ? (c.system_name || 'Notificações CRM').toLowerCase().includes(q)
      : c.other_user.name?.toLowerCase().includes(q)
  );
});

const filteredUsers = computed(() => {
  const q = searchQuery.value.toLowerCase();
  return availableUsers.value.filter(
    u =>
      !q ||
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q)
  );
});

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'agora';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  if (diff < 86400000)
    return d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
}

async function startConversation(userId) {
  const conv = await store.dispatch(
    'internalChat/createConversation',
    userId
  );
  showUserPicker.value = false;
  searchQuery.value = '';
  store.dispatch('internalChat/setActiveConversation', conv.id);
}

onMounted(() => {
  store.dispatch('internalChat/fetchConversations');
  store.dispatch('internalChat/fetchAvailableUsers');
});
</script>
